import { groupBy, mapValues } from 'lodash';
import type {
  APIFieldAttributes,
  APIReferenceType,
  APITagAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { dateRangeOperations } from '@ardoq/date-range';
import type {
  PerspectivesGroupsComponentTypes,
  PerspectivesGroupsFields,
  PerspectivesGroupsReferenceTypes,
  PerspectivesGroupsTags,
  PerspectivesGroupsWorkspace,
} from '@ardoq/perspectives';
import type { WorkspaceWithTypes } from '@ardoq/scope-data';

export type VirtualReferenceTypesByWorkspace = Record<
  ArdoqId,
  Record<string, APIReferenceType>
>;

export const getComponentTypeOptionsPerWorkspace = (
  workspacesWithTypes: WorkspaceWithTypes[],
  componentTypeNames?: Set<string>
): Record<ArdoqId, PerspectivesGroupsComponentTypes[]> => {
  const result: Record<string, { id: string; name: string }[]> = {};

  for (const { workspace, componentTypes } of workspacesWithTypes) {
    const componentTypeOptions: { id: string; name: string }[] = [];

    for (const type of componentTypes) {
      if (componentTypeNames && !componentTypeNames.has(type.name)) {
        continue;
      }
      componentTypeOptions.push({
        id: type.id,
        name: type.name,
      });
    }

    result[workspace._id] = componentTypeOptions;
  }

  return result;
};

export const getWorkspaceOptions = (
  workspacesWithTypes: WorkspaceWithTypes[]
): PerspectivesGroupsWorkspace[] => {
  return workspacesWithTypes.map(({ workspace }) => ({
    id: workspace._id,
    name: workspace.name,
  }));
};

export const getReferenceTypeOptionsPerWorkspace = (
  workspacesWithTypes: WorkspaceWithTypes[],
  virtualReferenceTypesByWorkspace: VirtualReferenceTypesByWorkspace,
  referenceTypeNames?: Set<string>
): Record<ArdoqId, PerspectivesGroupsReferenceTypes[]> => {
  const result: Record<string, { id: number; name: string }[]> = {};

  for (const { workspace, referenceTypes } of workspacesWithTypes) {
    const virtualReferenceTypes =
      virtualReferenceTypesByWorkspace[workspace._id] ?? {};

    const typeOptions: { id: number; name: string }[] = [];

    for (const type of referenceTypes) {
      if (referenceTypeNames && !referenceTypeNames.has(type.name)) {
        continue;
      }
      typeOptions.push({
        id: type.id,
        name: type.name,
      });
    }

    for (const typeId in virtualReferenceTypes ?? {}) {
      const type = virtualReferenceTypes![typeId];
      if (referenceTypeNames && !referenceTypeNames.has(type.name)) {
        continue;
      }
      typeOptions.push({
        id: type.id,
        name: type.name,
      });
    }

    result[workspace._id] = typeOptions;
  }

  return result;
};

export const toFieldOptions = (
  fields: APIFieldAttributes[]
): PerspectivesGroupsFields[] =>
  dateRangeOperations
    .mergeDateTimeFieldsToDateRangeFields(fields)
    .fields.flatMap(dateRangeOperations.getFieldIdAndLabel)
    .map(({ id, label }) => ({ id, name: label }));

const tagToTagOption = (tag: APITagAttributes): PerspectivesGroupsTags => ({
  id: tag._id,
  name: tag.name,
});

export const groupTagOptionsPerWorkspace = (
  tags: APITagAttributes[],
  workspacesWithTypes: WorkspaceWithTypes[]
): Record<ArdoqId, PerspectivesGroupsTags[]> => {
  const workspaceIds = workspacesWithTypes.map(
    ({ workspace }) => workspace._id
  );
  const tagsPerWorkspace = groupBy(
    tags.filter(({ rootWorkspace }) => workspaceIds.includes(rootWorkspace)),
    'rootWorkspace'
  );

  return mapValues(tagsPerWorkspace, tags => tags.map(tagToTagOption));
};
